import { useEffect } from 'react';
import { Box, Drawer, IconButton, InputAdornment, Stack, Typography, useTheme } from '@mui/material';
import { Control, useForm } from 'react-hook-form';
import { IoClose } from 'react-icons/io5';
import Input from 'components/Common/Input';
import Select from 'components/Common/Select';
import Switch from 'components/Common/Switch';
import Button from 'components/Common/Button';
import RichTextInput from 'components/Common/RichTextInput';
import { AgencyAddon } from '@/apis/agency/addons/types';
import { useUpdateAgencyAddon } from '@/apis/agency/addons/hook';
import { COMMITMENT_OPTIONS, getAddonDisplayTitle } from './addonDisplay';

interface AddonEditFormValues {
  active: boolean;
  commitment_months: number;
  description: string;
  prices: {
    price_per_location?: number | string;
    minimum_locations_billed?: number | string;
    one_time_setup_fee?: number | string;
    price_per_hosted_number?: number | string;
    vetting_fee_per_campaign?: number | string;
    mixed_campaign_per_month?: number | string;
    low_volume_mixed_campaign?: number | string;
    max_numbers_per_campaign?: number | string;
    monthly_price_all_locations?: number | string;
    included_runs_per_month?: number | string;
    price_per_additional_run?: number | string;
  };
}

interface AddonEditDrawerProps {
  addon: AgencyAddon | null;
  open: boolean;
  onClose: () => void;
}

const moneyAdornment = {
  startAdornment: <InputAdornment position='start'>$</InputAdornment>
};

function toNumber(value: number | string | undefined): number {
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

function priceValue(prices: Record<string, number | string> | undefined, key: string): number | string {
  const raw = prices?.[key];
  return raw === undefined || raw === null ? '' : raw;
}

function PrioritySupportFields({ control }: { control: Control<AddonEditFormValues> }) {
  return (
    <Box mb={0.5}>
      <Typography fontWeight={500} mb={1.5}>
        Pricing
      </Typography>
      <Box display='grid' gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
        <Input
          name='prices.one_time_setup_fee'
          control={control}
          type='number'
          label='One-Time Setup Fee'
          fullWidth
          inputProps={{ min: 0, step: '0.01' }}
          InputProps={moneyAdornment}
        />
        <Input
          name='prices.price_per_location'
          control={control}
          type='number'
          label='Price Per Location (Monthly)'
          fullWidth
          inputProps={{ min: 0, step: '0.01' }}
          InputProps={moneyAdornment}
        />
      </Box>
      <Box mt={2}>
        <Input
          name='prices.minimum_locations_billed'
          control={control}
          type='number'
          label='Minimum Locations Billed'
          fullWidth
          inputProps={{ min: 0, step: '1' }}
        />
      </Box>
    </Box>
  );
}

function SmsHostingFields({ control }: { control: Control<AddonEditFormValues> }) {
  return (
    <Box mb={0.5}>
      <Typography fontWeight={500} mb={1.5}>
        Pricing
      </Typography>
      <Box display='grid' gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
        <Input
          name='prices.one_time_setup_fee'
          control={control}
          type='number'
          label='One-Time Setup Fee per Number'
          fullWidth
          inputProps={{ min: 0, step: '0.01' }}
          InputProps={moneyAdornment}
        />
        <Input
          name='prices.price_per_hosted_number'
          control={control}
          type='number'
          label='Price Per Hosted Number (Monthly)'
          fullWidth
          inputProps={{ min: 0, step: '0.01' }}
          InputProps={moneyAdornment}
        />
      </Box>
    </Box>
  );
}

function A2pFields({ control }: { control: Control<AddonEditFormValues> }) {
  return (
    <Stack gap={2.5}>
      <Box>
        <Typography fontWeight={500} mb={1.5}>
          Registration
        </Typography>
        <Stack direction='row' gap={2}>
          <Input
            name='prices.one_time_setup_fee'
            control={control}
            type='number'
            label='One-Time Setup Fee'
            fullWidth
            inputProps={{ min: 0, step: '0.01' }}
            InputProps={moneyAdornment}
          />
          <Input
            name='prices.vetting_fee_per_campaign'
            control={control}
            type='number'
            label='Vetting Fee Per Campaign'
            fullWidth
            helperText='Charged for each campaign submission and resubmission.'
            inputProps={{ min: 0, step: '0.01' }}
            InputProps={moneyAdornment}
          />
        </Stack>
      </Box>
      <Box>
        <Typography fontWeight={500} mb={1.5}>
          Campaign pricing (per campaign / month)
        </Typography>
        <Stack gap={2}>
          <Stack direction='row' gap={2}>
            <Input
              name='prices.mixed_campaign_per_month'
              control={control}
              type='number'
              label='Mixed Campaign'
              fullWidth
              helperText='Recommended for high-volume campaigns. It costs $20/month through Bandwidth.'
              inputProps={{ min: 0, step: '0.01' }}
              InputProps={moneyAdornment}
            />
            <Input
              name='prices.low_volume_mixed_campaign'
              control={control}
              type='number'
              label='Low Volume Mixed Campaign'
              fullWidth
              helperText='Recommended for small businesses with lower messaging volumes. It costs $3/month through Bandwidth.'
              inputProps={{ min: 0, step: '0.01' }}
              InputProps={moneyAdornment}
            />
          </Stack>
          <Input
            name='prices.max_numbers_per_campaign'
            control={control}
            type='number'
            label='Max Numbers per Campaign'
            fullWidth
            helperText='Maximum 49 phone numbers can be added to a campaign.'
            inputProps={{ min: 1, max: 49, step: '1' }}
            rules={{
              required: 'Max numbers per campaign is required',
              min: { value: 1, message: 'Must be at least 1' },
              max: { value: 49, message: 'Maximum 49 phone numbers can be added to a campaign.' },
              validate: (value: number | string) => {
                const numeric = typeof value === 'number' ? value : Number(value);
                if (!Number.isFinite(numeric)) return 'Enter a valid number';
                if (numeric > 49) return 'Maximum 49 phone numbers can be added to a campaign.';
                return true;
              }
            }}
          />
        </Stack>
      </Box>
    </Stack>
  );
}

function AiWorkflowFields({ control }: { control: Control<AddonEditFormValues> }) {
  return (
    <Box mb={0.5}>
      <Typography fontWeight={500} mb={1.5}>
        Registration
      </Typography>
      <Box display='grid' gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
        <Input
          name='prices.one_time_setup_fee'
          control={control}
          type='number'
          label='One-Time Setup Fee'
          fullWidth
          inputProps={{ min: 0, step: '0.01' }}
          InputProps={moneyAdornment}
        />
        <Input
          name='prices.monthly_price_all_locations'
          control={control}
          type='number'
          label='Monthly Price (All Locations)'
          fullWidth
          inputProps={{ min: 0, step: '0.01' }}
          InputProps={moneyAdornment}
        />
        <Input
          name='prices.included_runs_per_month'
          control={control}
          type='number'
          label='Included Runs / Month'
          fullWidth
          inputProps={{ min: 0, step: '1' }}
        />
        <Input
          name='prices.price_per_additional_run'
          control={control}
          type='number'
          label='Price Per Additional Run'
          fullWidth
          inputProps={{ min: 0, step: '0.01' }}
          InputProps={moneyAdornment}
        />
      </Box>
    </Box>
  );
}

function buildPricesPayload(code: string, prices: AddonEditFormValues['prices']): Record<string, number> {
  switch (code) {
    case 'priority_support':
      return {
        one_time_setup_fee: toNumber(prices.one_time_setup_fee),
        price_per_location: toNumber(prices.price_per_location),
        minimum_locations_billed: toNumber(prices.minimum_locations_billed)
      };
    case 'sms_hosting':
      return {
        one_time_setup_fee: toNumber(prices.one_time_setup_fee),
        price_per_hosted_number: toNumber(prices.price_per_hosted_number)
      };
    case 'a2p_10dlc':
      return {
        one_time_setup_fee: toNumber(prices.one_time_setup_fee),
        vetting_fee_per_campaign: toNumber(prices.vetting_fee_per_campaign),
        mixed_campaign_per_month: toNumber(prices.mixed_campaign_per_month),
        low_volume_mixed_campaign: toNumber(prices.low_volume_mixed_campaign),
        max_numbers_per_campaign: Math.min(49, Math.max(1, toNumber(prices.max_numbers_per_campaign)))
      };
    case 'ai_workflow':
      return {
        one_time_setup_fee: toNumber(prices.one_time_setup_fee),
        monthly_price_all_locations: toNumber(prices.monthly_price_all_locations),
        included_runs_per_month: toNumber(prices.included_runs_per_month),
        price_per_additional_run: toNumber(prices.price_per_additional_run)
      };
    default:
      return {};
  }
}

export default function AddonEditDrawer({ addon, open, onClose }: AddonEditDrawerProps) {
  const theme = useTheme();
  const { mutateAsync: updateAddon, isPending } = useUpdateAgencyAddon();

  const { control, handleSubmit, reset } = useForm<AddonEditFormValues>({
    defaultValues: {
      active: false,
      commitment_months: 0,
      description: '',
      prices: {}
    }
  });

  useEffect(() => {
    if (!addon || !open) return;

    const prices = addon.prices || {};

    reset({
      active: !!addon.active,
      commitment_months: Number(addon.commitment_months) || 0,
      description: addon.description || '',
      prices: {
        price_per_location: priceValue(prices, 'price_per_location'),
        minimum_locations_billed: priceValue(prices, 'minimum_locations_billed'),
        one_time_setup_fee: priceValue(prices, 'one_time_setup_fee'),
        price_per_hosted_number: priceValue(prices, 'price_per_hosted_number'),
        vetting_fee_per_campaign: priceValue(prices, 'vetting_fee_per_campaign'),
        mixed_campaign_per_month: priceValue(prices, 'mixed_campaign_per_month'),
        low_volume_mixed_campaign: priceValue(prices, 'low_volume_mixed_campaign'),
        max_numbers_per_campaign: priceValue(prices, 'max_numbers_per_campaign'),
        monthly_price_all_locations: priceValue(prices, 'monthly_price_all_locations'),
        included_runs_per_month: priceValue(prices, 'included_runs_per_month'),
        price_per_additional_run: priceValue(prices, 'price_per_additional_run')
      }
    });
  }, [addon, open, reset]);

  const title = addon ? getAddonDisplayTitle(addon) : '';
  const code = addon?.code;

  const onSubmit = async (values: AddonEditFormValues) => {
    if (!addon) return;

    await updateAddon({
      id: addon.id,
      payload: {
        addon: {
          active: values.active,
          commitment_months: Number(values.commitment_months) || 0,
          description: values.description,
          prices: buildPricesPayload(addon.code, values.prices)
        }
      }
    });
    onClose();
  };

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 600,
          maxWidth: '100%',
          p: 0,
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        px={3}
        py={2}
        borderBottom='1px solid'
        borderColor='divider'
        sx={{ position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper' }}
      >
        <Typography variant='h6' fontWeight={500}>
          {title}
        </Typography>
        <IconButton onClick={onClose} size='small' aria-label='Close'>
          <IoClose size={22} />
        </IconButton>
      </Box>

      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
      >
        <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2.5 }}>
          <Stack direction='row' alignItems='center' justifyContent='space-between' gap={2} mb={2.5}>
            <Box>
              <Typography fontWeight={500}>Offer this Add-On</Typography>
              <Typography fontSize={13} color='text.secondary' mt={0.25}>
                Businesses can see and enable it from their Add-ons page
              </Typography>
            </Box>
            <Switch name='active' control={control} />
          </Stack>

          {code === 'ai_workflow' && (
            <Box
              mb={2.5}
              sx={{
                backgroundColor: theme.palette.primary.light,
                borderRadius: 1.5,
                p: 1.5,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1
              }}
            >
              <Typography fontSize={13} color={theme.palette.primary.main}>
                <Box component='span' fontWeight={500}>
                  Note:{' '}
                </Box>
                Workflows are configured per the business&apos;s needs. Available workflow possibilities depend on
                integration API availability.
              </Typography>
            </Box>
          )}

          {code === 'priority_support' && <PrioritySupportFields control={control} />}
          {code === 'sms_hosting' && <SmsHostingFields control={control} />}
          {code === 'a2p_10dlc' && <A2pFields control={control} />}
          {code === 'ai_workflow' && <AiWorkflowFields control={control} />}

          <Typography fontWeight={500} my={2}>
            Terms & Offering
          </Typography>
          <Select
            name='commitment_months'
            control={control}
            label='Commitment'
            fullWidth
            options={COMMITMENT_OPTIONS}
            helperText='The business will be charged if they cancel before the commitment period ends.'
          />

          <Box mt={2.5}>
            <RichTextInput
              name='description'
              control={control}
              helperText='Shown to businesses on the add-on details page before they enable it.'
            />
          </Box>
        </Box>

        <Box
          display='flex'
          justifyContent='flex-end'
          gap={1.5}
          px={3}
          py={2}
          borderTop='1px solid'
          borderColor='divider'
          sx={{ bgcolor: 'background.paper' }}
        >
          <Button variant='outlined' color='inherit' onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button type='submit' variant='contained' isPending={isPending}>
            Save
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
